import { generateClient } from 'aws-amplify/api';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

// Badge condition types
export const BADGE_CONDITION_TYPES = {
  // Social engagement
  FOLLOWER_COUNT: 'follower_count',
  FOLLOWING_COUNT: 'following_count',
  POSTS_CREATED: 'posts_created',
  LIKES_GIVEN: 'likes_given',
  LIKES_RECEIVED: 'likes_received',
  COMMENTS_MADE: 'comments_made',
  SHARES_MADE: 'shares_made',
  
  // Content specific
  SPECIFIC_MOVIE_WATCHED: 'specific_movie_watched',
  SPECIFIC_SHOW_WATCHED: 'specific_show_watched',
  MOVIE_GROUP_WATCHED: 'movie_group_watched',
  SHOW_GROUP_WATCHED: 'show_group_watched',
  FRANCHISE_WATCHED: 'franchise_watched',
  GENRE_WATCHED_COUNT: 'genre_watched_count',
  
  // Activity based
  REVIEWS_WRITTEN: 'reviews_written',
  MOVIES_WATCHED: 'movies_watched',
  SHOWS_WATCHED: 'shows_watched',
  WATCHLIST_SIZE: 'watchlist_size',
  RATINGS_GIVEN: 'ratings_given',
  
  // Time based
  DAYS_ACTIVE: 'days_active',
  LOGIN_STREAK: 'login_streak',
  ACCOUNT_AGE: 'account_age',
  
  // Hype tracking
  EARLY_DISCOVERIES: 'early_discoveries',
  TRENDING_PREDICTIONS: 'trending_predictions',
  
  // Seasonal/Special
  SEASONAL_ACTIVITY: 'seasonal_activity',
  EVENT_PARTICIPATION: 'event_participation'
};

// Predefined badge templates
export const AUTOMATED_BADGE_TEMPLATES = {
  // Social Engagement Badges
  SOCIAL_BUTTERFLY: {
    name: 'Social Butterfly',
    description: 'Like 100 posts in a week',
    icon: 'Favorite',
    color: '#E91E63',
    rarity: 'uncommon',
    points: 25,
    conditions: {
      type: BADGE_CONDITION_TYPES.LIKES_GIVEN,
      threshold: 100,
      timeframe: 'week'
    }
  },
  
  POPULAR: {
    name: 'Popular',
    description: 'Reach 1000 followers',
    icon: 'People',
    color: '#9C27B0',
    rarity: 'rare',
    points: 50,
    conditions: {
      type: BADGE_CONDITION_TYPES.FOLLOWER_COUNT,
      threshold: 1000
    }
  },
  
  INFLUENCER: {
    name: 'Influencer',
    description: 'Reach 10,000 followers',
    icon: 'Star',
    color: '#FF9800',
    rarity: 'epic',
    points: 200,
    conditions: {
      type: BADGE_CONDITION_TYPES.FOLLOWER_COUNT,
      threshold: 10000
    }
  },
  
  CELEBRITY: {
    name: 'Celebrity',
    description: 'Reach 100,000 followers',
    icon: 'WorkspacePremium',
    color: '#FFD700',
    rarity: 'legendary',
    points: 1000,
    conditions: {
      type: BADGE_CONDITION_TYPES.FOLLOWER_COUNT,
      threshold: 100000
    }
  },
  
  // Content Creation Badges
  CONTENT_CREATOR: {
    name: 'Content Creator',
    description: 'Create 100 posts',
    icon: 'Create',
    color: '#2196F3',
    rarity: 'uncommon',
    points: 30,
    conditions: {
      type: BADGE_CONDITION_TYPES.POSTS_CREATED,
      threshold: 100
    }
  },
  
  DAILY_POSTER: {
    name: 'Daily Poster',
    description: 'Post every day for 30 days',
    icon: 'Today',
    color: '#4CAF50',
    rarity: 'rare',
    points: 75,
    conditions: {
      type: BADGE_CONDITION_TYPES.POSTS_CREATED,
      threshold: 30,
      timeframe: 'daily_streak'
    }
  },
  
  // Movie Specific Badges
  AVENGERS_FAN: {
    name: 'Avengers Fan',
    description: 'Watch all Avengers movies',
    icon: 'Movie',
    color: '#F44336',
    rarity: 'rare',
    points: 100,
    conditions: {
      type: BADGE_CONDITION_TYPES.MOVIE_GROUP_WATCHED,
      movieIds: ['24428', '99861', '299536', '299534'] // Avengers movie IDs
    }
  },
  
  MCU_COMPLETIONIST: {
    name: 'MCU Completionist',
    description: 'Watch all MCU Phase 1 movies',
    icon: 'CollectionsBookmark',
    color: '#FF5722',
    rarity: 'epic',
    points: 250,
    conditions: {
      type: BADGE_CONDITION_TYPES.MOVIE_GROUP_WATCHED,
      movieIds: ['1726', '1724', '1725', '10195', '1771', '24428'] // MCU Phase 1
    }
  },
  
  HORROR_ENTHUSIAST: {
    name: 'Horror Enthusiast',
    description: 'Watch 50 horror movies',
    icon: 'Nightlife',
    color: '#9C27B0',
    rarity: 'rare',
    points: 100,
    conditions: {
      type: BADGE_CONDITION_TYPES.GENRE_WATCHED_COUNT,
      genre: 'horror',
      threshold: 50
    }
  },
  
  ACTION_HERO: {
    name: 'Action Hero',
    description: 'Watch 100 action movies',
    icon: 'LocalFireDepartment',
    color: '#FF5722',
    rarity: 'epic',
    points: 150,
    conditions: {
      type: BADGE_CONDITION_TYPES.GENRE_WATCHED_COUNT,
      genre: 'action',
      threshold: 100
    }
  },
  
  // Show Specific Badges
  BINGE_WATCHER: {
    name: 'Binge Watcher',
    description: 'Watch 10 complete TV series',
    icon: 'Tv',
    color: '#3F51B5',
    rarity: 'rare',
    points: 100,
    conditions: {
      type: BADGE_CONDITION_TYPES.SHOWS_WATCHED,
      threshold: 10
    }
  },
  
  SERIES_ADDICT: {
    name: 'Series Addict',
    description: 'Watch 50 complete TV series',
    icon: 'LiveTv',
    color: '#673AB7',
    rarity: 'epic',
    points: 300,
    conditions: {
      type: BADGE_CONDITION_TYPES.SHOWS_WATCHED,
      threshold: 50
    }
  },
  
  // Review & Rating Badges
  CRITIC: {
    name: 'Critic',
    description: 'Write 50 detailed reviews',
    icon: 'RateReview',
    color: '#795548',
    rarity: 'rare',
    points: 75,
    conditions: {
      type: BADGE_CONDITION_TYPES.REVIEWS_WRITTEN,
      threshold: 50
    }
  },
  
  RATING_MACHINE: {
    name: 'Rating Machine',
    description: 'Rate 500 movies/shows',
    icon: 'Star',
    color: '#FFC107',
    rarity: 'rare',
    points: 100,
    conditions: {
      type: BADGE_CONDITION_TYPES.RATINGS_GIVEN,
      threshold: 500
    }
  },
  
  // Streak Badges
  STREAK_STARTER: {
    name: 'Streak Starter',
    description: 'Login for 7 consecutive days',
    icon: 'LocalFireDepartment',
    color: '#FF5722',
    rarity: 'common',
    points: 25,
    conditions: {
      type: BADGE_CONDITION_TYPES.LOGIN_STREAK,
      threshold: 7
    }
  },
  
  STREAK_MASTER: {
    name: 'Streak Master',
    description: 'Login for 30 consecutive days',
    icon: 'LocalFireDepartment',
    color: '#FF9800',
    rarity: 'rare',
    points: 100,
    conditions: {
      type: BADGE_CONDITION_TYPES.LOGIN_STREAK,
      threshold: 30
    }
  },
  
  STREAK_LEGEND: {
    name: 'Streak Legend',
    description: 'Login for 100 consecutive days',
    icon: 'LocalFireDepartment',
    color: '#FFD700',
    rarity: 'legendary',
    points: 500,
    conditions: {
      type: BADGE_CONDITION_TYPES.LOGIN_STREAK,
      threshold: 100
    }
  },
  
  // Hype Hunter Badges
  EARLY_BIRD: {
    name: 'Early Bird',
    description: 'Discover 10 trending movies before they peak',
    icon: 'TrendingUp',
    color: '#4CAF50',
    rarity: 'uncommon',
    points: 50,
    conditions: {
      type: BADGE_CONDITION_TYPES.EARLY_DISCOVERIES,
      threshold: 10
    }
  },
  
  HYPE_HUNTER: {
    name: 'Hype Hunter',
    description: 'Discover 25 trending movies before they peak',
    icon: 'Search',
    color: '#2196F3',
    rarity: 'rare',
    points: 125,
    conditions: {
      type: BADGE_CONDITION_TYPES.EARLY_DISCOVERIES,
      threshold: 25
    }
  },
  
  TREND_PROPHET: {
    name: 'Trend Prophet',
    description: 'Discover 50 trending movies before they peak',
    icon: 'Visibility',
    color: '#9C27B0',
    rarity: 'epic',
    points: 300,
    conditions: {
      type: BADGE_CONDITION_TYPES.EARLY_DISCOVERIES,
      threshold: 50
    }
  },
  
  // Loyalty Badges
  VETERAN: {
    name: 'Veteran',
    description: 'Active for 1 year',
    icon: 'Shield',
    color: '#607D8B',
    rarity: 'rare',
    points: 100,
    conditions: {
      type: BADGE_CONDITION_TYPES.ACCOUNT_AGE,
      threshold: 365 // days
    }
  },
  
  LEGEND: {
    name: 'Legend',
    description: 'Active for 3 years',
    icon: 'EmojiEvents',
    color: '#FFD700',
    rarity: 'legendary',
    points: 500,
    conditions: {
      type: BADGE_CONDITION_TYPES.ACCOUNT_AGE,
      threshold: 1095 // days
    }
  },
  
  // Seasonal Badges
  HALLOWEEN_HORROR: {
    name: 'Halloween Horror',
    description: 'Watch 31 horror movies in October',
    icon: 'Nightlife',
    color: '#FF6D00',
    rarity: 'epic',
    points: 200,
    conditions: {
      type: BADGE_CONDITION_TYPES.SEASONAL_ACTIVITY,
      genre: 'horror',
      threshold: 31,
      month: 10
    }
  },
  
  HOLIDAY_SPIRIT: {
    name: 'Holiday Spirit',
    description: 'Watch 25 holiday movies in December',
    icon: 'CelebrationOutlined',
    color: '#4CAF50',
    rarity: 'rare',
    points: 100,
    conditions: {
      type: BADGE_CONDITION_TYPES.SEASONAL_ACTIVITY,
      genre: 'holiday',
      threshold: 25,
      month: 12
    }
  }
};

export const automatedBadgeService = {
  // Check if user qualifies for any automated badges
  async checkUserForBadges(userId, userStats = null) {
    try {
      console.log('Checking badges for user:', userId);
      
      // Get user stats if not provided
      if (!userStats) {
        userStats = await this.getUserStats(userId);
      }
      
      // Get existing user badges
      const existingBadges = await this.getUserBadges(userId);
      const existingBadgeTypes = existingBadges.map(b => b.badgeType);
      
      // Get all active automated badge definitions
      const badgeDefinitions = await this.getAutomatedBadgeDefinitions();
      
      const newBadges = [];
      
      for (const badge of badgeDefinitions) {
        // Skip if user already has this badge
        if (existingBadgeTypes.includes(badge.type)) {
          continue;
        }
        
        // Check if user qualifies for this badge
        const qualifies = await this.checkBadgeCondition(badge, userStats, userId);
        
        if (qualifies) {
          console.log(`User ${userId} qualifies for badge: ${badge.name}`);
          
          // Grant the badge
          const grantedBadge = await this.grantBadge(userId, badge.type);
          if (grantedBadge) {
            newBadges.push({
              ...badge,
              grantedAt: grantedBadge.earnedAt
            });
          }
        }
      }
      
      return newBadges;
    } catch (error) {
      console.error('Error checking user for badges:', error);
      return [];
    }
  },
  
  // Check specific badge condition
  async checkBadgeCondition(badge, userStats, userId) {
    try {
      const conditions = badge.conditions;
      
      switch (conditions.type) {
        case BADGE_CONDITION_TYPES.FOLLOWER_COUNT:
          return userStats.followersCount >= conditions.threshold;
          
        case BADGE_CONDITION_TYPES.FOLLOWING_COUNT:
          return userStats.followingCount >= conditions.threshold;
          
        case BADGE_CONDITION_TYPES.POSTS_CREATED:
          if (conditions.timeframe === 'daily_streak') {
            return await this.checkDailyPostStreak(userId, conditions.threshold);
          }
          return userStats.postsCount >= conditions.threshold;
          
        case BADGE_CONDITION_TYPES.LIKES_GIVEN:
          if (conditions.timeframe === 'week') {
            return await this.checkWeeklyLikes(userId, conditions.threshold);
          }
          return userStats.likesGiven >= conditions.threshold;
          
        case BADGE_CONDITION_TYPES.REVIEWS_WRITTEN:
          return userStats.reviewsCount >= conditions.threshold;
          
        case BADGE_CONDITION_TYPES.MOVIES_WATCHED:
          return userStats.moviesWatched >= conditions.threshold;
          
        case BADGE_CONDITION_TYPES.SHOWS_WATCHED:
          return userStats.showsWatched >= conditions.threshold;
          
        case BADGE_CONDITION_TYPES.RATINGS_GIVEN:
          return userStats.ratingsGiven >= conditions.threshold;
          
        case BADGE_CONDITION_TYPES.MOVIE_GROUP_WATCHED:
          return await this.checkMovieGroupWatched(userId, conditions.movieIds);
          
        case BADGE_CONDITION_TYPES.SHOW_GROUP_WATCHED:
          return await this.checkShowGroupWatched(userId, conditions.showIds);
          
        case BADGE_CONDITION_TYPES.GENRE_WATCHED_COUNT:
          return await this.checkGenreWatchedCount(userId, conditions.genre, conditions.threshold);
          
        case BADGE_CONDITION_TYPES.ACCOUNT_AGE:
          const accountAge = Math.floor((Date.now() - new Date(userStats.createdAt).getTime()) / (1000 * 60 * 60 * 24));
          return accountAge >= conditions.threshold;
          
        case BADGE_CONDITION_TYPES.LOGIN_STREAK:
          return await this.checkLoginStreak(userId, conditions.threshold);
          
        case BADGE_CONDITION_TYPES.EARLY_DISCOVERIES:
          return userStats.earlyDiscoveries >= conditions.threshold;
          
        case BADGE_CONDITION_TYPES.TRENDING_PREDICTIONS:
          return userStats.trendingPredictions >= conditions.threshold;
          
        case BADGE_CONDITION_TYPES.SEASONAL_ACTIVITY:
          return await this.checkSeasonalActivity(userId, conditions);
          
        default:
          console.warn('Unknown badge condition type:', conditions.type);
          return false;
      }
    } catch (error) {
      console.error('Error checking badge condition:', error);
      return false;
    }
  },
  
  // Helper methods for specific checks
  async checkMovieGroupWatched(userId, movieIds) {
    try {
      const watchedMovies = await this.getUserWatchedMovies(userId);
      return movieIds.every(movieId => watchedMovies.includes(movieId));
    } catch (error) {
      console.error('Error checking movie group:', error);
      return false;
    }
  },
  
  async checkShowGroupWatched(userId, showIds) {
    try {
      const watchedShows = await this.getUserWatchedShows(userId);
      return showIds.every(showId => watchedShows.includes(showId));
    } catch (error) {
      console.error('Error checking show group:', error);
      return false;
    }
  },
  
  async checkGenreWatchedCount(userId, genre, threshold) {
    try {
      const genreCount = await this.getUserGenreWatchCount(userId, genre);
      return genreCount >= threshold;
    } catch (error) {
      console.error('Error checking genre count:', error);
      return false;
    }
  },
  
  async checkDailyPostStreak(userId, requiredDays) {
    try {
      // Implementation would check for consecutive daily posts
      // This is a simplified version
      const recentPosts = await this.getUserRecentPosts(userId, requiredDays);
      const uniqueDays = new Set(recentPosts.map(post => 
        new Date(post.createdAt).toDateString()
      ));
      return uniqueDays.size >= requiredDays;
    } catch (error) {
      console.error('Error checking daily post streak:', error);
      return false;
    }
  },
  
  async checkWeeklyLikes(userId, threshold) {
    try {
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const weeklyLikes = await this.getUserLikesSince(userId, weekAgo);
      return weeklyLikes >= threshold;
    } catch (error) {
      console.error('Error checking weekly likes:', error);
      return false;
    }
  },
  
  async checkLoginStreak(userId, requiredDays) {
    try {
      // Get user's login streak from user stats or calculate from interaction events
      const userStats = await this.getUserStats(userId);
      return (userStats.loginStreak || 0) >= requiredDays;
    } catch (error) {
      console.error('Error checking login streak:', error);
      return false;
    }
  },
  
  async checkSeasonalActivity(userId, conditions) {
    try {
      const currentMonth = new Date().getMonth() + 1;
      if (currentMonth !== conditions.month) {
        return false;
      }
      
      const monthStart = new Date(new Date().getFullYear(), conditions.month - 1, 1).toISOString();
      const monthEnd = new Date(new Date().getFullYear(), conditions.month, 0).toISOString();
      
      const monthlyActivity = await this.getUserMonthlyGenreActivity(
        userId, 
        conditions.genre, 
        monthStart, 
        monthEnd
      );
      
      return monthlyActivity >= conditions.threshold;
    } catch (error) {
      console.error('Error checking seasonal activity:', error);
      return false;
    }
  },
  
  // Data fetching methods
  async getUserStats(userId) {
    try {
      const GET_USER_STATS = `
        query GetUser($id: ID!) {
          getUser(id: $id) {
            id
            followersCount
            followingCount
            postsCount
            likesGiven
            reviewsCount
            moviesWatched
            showsWatched
            ratingsGiven
            loginStreak
            earlyDiscoveries
            trendingPredictions
            createdAt
          }
        }
      `;
      
      const result = await getClient().graphql({
        query: GET_USER_STATS,
        variables: { id: userId }
      });
      
      return result.data.getUser || {};
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return {};
    }
  },
  
  async getUserBadges(userId) {
    try {
      const LIST_USER_BADGES = `
        query ListUserBadges($userId: ID!) {
          listUserBadges(filter: { userID: { eq: $userId } }) {
            items {
              id
              userID
              badgeType
              earnedAt
            }
          }
        }
      `;
      
      const result = await getClient().graphql({
        query: LIST_USER_BADGES,
        variables: { userId }
      });
      
      return result.data.listUserBadges.items || [];
    } catch (error) {
      console.error('Error fetching user badges:', error);
      return [];
    }
  },
  
  async getAutomatedBadgeDefinitions() {
    try {
      const LIST_BADGE_DEFINITIONS = `
        query ListBadgeDefinitions {
          listBadgeDefinitions(filter: { autoAssign: { eq: true }, isActive: { eq: true } }) {
            items {
              id
              type
              name
              description
              icon
              color
              rarity
              points
              conditions
              autoAssign
              isActive
            }
          }
        }
      `;
      
      const result = await getClient().graphql({
        query: LIST_BADGE_DEFINITIONS
      });
      
      return result.data.listBadgeDefinitions.items.map(item => ({
        ...item,
        conditions: item.conditions ? JSON.parse(item.conditions) : {}
      })) || [];
    } catch (error) {
      console.error('Error fetching automated badge definitions:', error);
      return [];
    }
  },
  
  async getUserWatchedMovies(userId) {
    try {
      const LIST_WATCHED_MOVIES = `
        query ListWatchlists($userId: ID!) {
          listWatchlists(filter: { 
            userID: { eq: $userId }, 
            status: { eq: "watched" },
            type: { eq: "movie" }
          }) {
            items {
              movieID
            }
          }
        }
      `;
      
      const result = await getClient().graphql({
        query: LIST_WATCHED_MOVIES,
        variables: { userId }
      });
      
      return result.data.listWatchlists.items.map(item => item.movieID) || [];
    } catch (error) {
      console.error('Error fetching watched movies:', error);
      return [];
    }
  },
  
  async getUserWatchedShows(userId) {
    try {
      const LIST_WATCHED_SHOWS = `
        query ListWatchlists($userId: ID!) {
          listWatchlists(filter: { 
            userID: { eq: $userId }, 
            status: { eq: "watched" },
            type: { eq: "show" }
          }) {
            items {
              showID
            }
          }
        }
      `;
      
      const result = await getClient().graphql({
        query: LIST_WATCHED_SHOWS,
        variables: { userId }
      });
      
      return result.data.listWatchlists.items.map(item => item.showID) || [];
    } catch (error) {
      console.error('Error fetching watched shows:', error);
      return [];
    }
  },
  
  async getUserGenreWatchCount(userId, genre) {
    try {
      // This would require joining with movie/show data to get genres
      // Simplified implementation
      const watchedMovies = await this.getUserWatchedMovies(userId);
      // In real implementation, you'd fetch movie details and count by genre
      return Math.floor(watchedMovies.length * 0.2); // Placeholder
    } catch (error) {
      console.error('Error fetching genre watch count:', error);
      return 0;
    }
  },
  
  async grantBadge(userId, badgeType) {
    try {
      const CREATE_USER_BADGE = `
        mutation CreateUserBadge($input: CreateUserBadgeInput!) {
          createUserBadge(input: $input) {
            id
            userID
            badgeType
            earnedAt
          }
        }
      `;
      
      const input = {
        userID: userId,
        badgeType: badgeType,
        earnedAt: new Date().toISOString()
      };
      
      const result = await getClient().graphql({
        query: CREATE_USER_BADGE,
        variables: { input }
      });
      
      // Trigger badge notification
      const badgeDefinition = Object.values(AUTOMATED_BADGE_TEMPLATES)
        .find(b => b.name.toUpperCase().replace(/\s+/g, '_') === badgeType);
      
      if (badgeDefinition) {
        const event = new CustomEvent('badgeEarned', { 
          detail: {
            ...badgeDefinition,
            badgeType
          }
        });
        window.dispatchEvent(event);
      }
      
      return result.data.createUserBadge;
    } catch (error) {
      console.error('Error granting badge:', error);
      return null;
    }
  },
  
  // Initialize automated badge definitions
  async initializeAutomatedBadges() {
    try {
      console.log('Initializing automated badge definitions...');
      
      const existingBadges = await this.getAutomatedBadgeDefinitions();
      const existingTypes = existingBadges.map(b => b.type);
      
      for (const [key, template] of Object.entries(AUTOMATED_BADGE_TEMPLATES)) {
        const badgeType = template.name.toUpperCase().replace(/\s+/g, '_');
        
        if (!existingTypes.includes(badgeType)) {
          await this.createBadgeDefinition({
            ...template,
            type: badgeType,
            autoAssign: true,
            isActive: true
          });
          console.log(`Created badge definition: ${template.name}`);
        }
      }
      
      console.log('Automated badge initialization complete');
    } catch (error) {
      console.error('Error initializing automated badges:', error);
    }
  },
  
  async createBadgeDefinition(badgeData) {
    try {
      const CREATE_BADGE_DEFINITION = `
        mutation CreateBadgeDefinition($input: CreateBadgeDefinitionInput!) {
          createBadgeDefinition(input: $input) {
            id
            type
            name
            description
            icon
            color
            rarity
            points
            isActive
            autoAssign
            conditions
          }
        }
      `;
      
      const input = {
        type: badgeData.type,
        name: badgeData.name,
        description: badgeData.description,
        icon: badgeData.icon,
        color: badgeData.color,
        rarity: badgeData.rarity,
        points: badgeData.points,
        isActive: badgeData.isActive,
        autoAssign: badgeData.autoAssign,
        conditions: JSON.stringify(badgeData.conditions)
      };
      
      const result = await getClient().graphql({
        query: CREATE_BADGE_DEFINITION,
        variables: { input }
      });
      
      return result.data.createBadgeDefinition;
    } catch (error) {
      console.error('Error creating badge definition:', error);
      throw error;
    }
  }
};